import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import { Card } from '@shared/ui/Card';
import type { Tone } from '../model';

/* Общие детали раздела «Заявки на оплату»: список, форма, карточка. */

export const Page = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(5)};
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(4)};
  flex-wrap: wrap;
`;

export const H1 = styled.h1`
  margin: 0;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.02em;
`;

export const Lead = styled.p`
  margin: ${({ theme }) => theme.spacing(2)} 0 0;
  max-width: 760px;
  font-size: 14px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const Muted = styled.div`
  font-size: 14px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const Small = styled.div`
  font-size: 12.5px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const ErrorText = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.status.danger};
`;

/** Плашка: успех, подсказка, «проверьте», ошибка. Цвет — из темы, фон — подмес. */
export const Notice = styled.div<{ $tone: Exclude<Tone, 'neutral'> }>`
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13.5px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme, $tone }) =>
    `color-mix(in srgb, ${theme.colors.status[$tone]} 12%, ${theme.colors.backgroundElevated} 88%)`};
  border-left: 3px solid ${({ theme, $tone }) => theme.colors.status[$tone]};

  & > strong:first-child {
    display: block;
    margin-bottom: ${({ theme }) => theme.spacing(1)};
  }

  & ul {
    margin: ${({ theme }) => theme.spacing(1)} 0 0;
    padding-left: ${({ theme }) => theme.spacing(5)};
  }
`;

export const NoticeRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

export const Tabs = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

export const Tab = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(4)}`};
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ theme, $active }) => ($active ? theme.colors.primarySoft : 'transparent')};
  color: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.text.secondary)};
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

/** Счётчик на вкладке: сколько ждёт внимания. */
export const TabCount = styled.span`
  min-width: 20px;
  padding: 0 6px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.colors.status.warning};
  color: #000;
  font-size: 11px;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
`;

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

/** Поле в панели фильтров: поиск шире, выпадающие списки — по содержимому. */
export const ToolbarItem = styled.div<{ $grow?: boolean }>`
  flex: ${({ $grow }) => ($grow ? '1 1 320px' : '0 1 240px')};
  min-width: 180px;
`;

export const SectionCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

export const SectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

export const SectionTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.01em;
`;

export const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

/** Крупный статус заявки в карточке. */
export const StagePill = styled.span<{ $tone: Tone }>`
  display: inline-flex;
  align-items: center;
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(4)}`};
  border-radius: ${({ theme }) => theme.radius.pill};
  font-size: 15px;
  font-weight: 700;
  ${({ theme, $tone }) =>
    $tone === 'neutral'
      ? css`
          background: ${theme.colors.surface};
          color: ${theme.colors.text.secondary};
          border: 1px solid ${theme.colors.border};
        `
      : css`
          background: ${theme.colors.status[$tone]}1A;
          color: ${theme.colors.status[$tone]};
          border: 1px solid ${theme.colors.status[$tone]}40;
        `}
`;

export const Flags = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

/** Поле формы: подпись, само поле, подсказка или ошибка — столбиком. */
export const FieldBox = styled.div<{ $wide?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  min-width: 0;
  grid-column: ${({ $wide }) => ($wide ? '1 / -1' : 'auto')};
`;

export const FieldCaption = styled.label`
  font-size: 12.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const FieldNote = styled.div<{ $error?: boolean }>`
  font-size: 12px;
  line-height: 1.4;
  color: ${({ theme, $error }) => ($error ? theme.colors.status.danger : theme.colors.text.muted)};
`;

export const CheckLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 13.5px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;

  & input {
    width: 16px;
    height: 16px;
    accent-color: ${({ theme }) => theme.colors.primary};
  }
`;

/** Строка заявки в списке — ссылка на карточку. */
export const RowLink = styled(Link)`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(4)}`};
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: inherit;
  text-decoration: none;
  transition:
    border-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }

  @media (max-width: 640px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const RowList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

export const RowTitle = styled.div`
  font-size: 14.5px;
  font-weight: 700;
  letter-spacing: -0.01em;
  overflow-wrap: anywhere;
`;

export const RowMeta = styled.div`
  font-size: 12.5px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
  overflow-wrap: anywhere;
`;

/** Назначение платежа в одну строку — целиком в карточке. */
export const RowPurpose = styled(RowMeta)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* Сумма и бейджи справа; бейджей много — переносятся, а не сжимают поставщика. */
export const RowSide = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing(1.5)};
  max-width: 340px;
  text-align: right;

  & > ${Flags} {
    justify-content: flex-end;
  }

  @media (max-width: 640px) {
    align-items: flex-start;
    max-width: none;
    text-align: left;

    & > ${Flags} {
      justify-content: flex-start;
    }
  }
`;

export const Amount = styled.div`
  font-size: 15px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

/** Заголовок группы «отдел» в общем списке. */
export const GroupHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
  padding-bottom: ${({ theme }) => theme.spacing(2)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

export const GroupTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
`;

export const GroupMeta = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-variant-numeric: tabular-nums;
`;

/** Сотрудник внутри отдела. */
export const PersonHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
  margin-top: ${({ theme }) => theme.spacing(2)};
  font-size: 14px;
  font-weight: 600;
`;

export const Group = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

/** Пары «подпись — значение» в карточке. */
export const InfoGrid = styled.dl`
  display: grid;
  grid-template-columns: minmax(140px, 220px) minmax(0, 1fr);
  gap: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(4)}`};
  margin: 0;
  font-size: 14px;
  line-height: 1.5;

  & dt {
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  & dd {
    margin: 0;
    overflow-wrap: anywhere;
  }

  @media (max-width: 640px) {
    grid-template-columns: minmax(0, 1fr);

    & dd {
      margin-bottom: ${({ theme }) => theme.spacing(2)};
    }
  }
`;

export const Mono = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 13px;
`;
